import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DocumentRequestAPI } from '../services/api';

// Public, mobile-first page where a customer uploads the requested documents via a
// document-request link. Files are staged in the browser and uploaded only on
// Submit. Matches the AMP Services customer brand (indigo gradient + white card);
// the signature element is the per-slot capture tiles with completion checkmarks.

interface Slot { key: string; label: string; }
interface Category { key: string; label: string; slots: string[]; slot_labels: Record<string, string>; formats: string[]; }

type Step = 'loading' | 'form' | 'submitting' | 'success' | 'error';

const isImageFormats = (formats: string[]) => formats.length === 1 && formats[0] === 'image';
const acceptFor = (formats: string[]) => (isImageFormats(formats) ? 'image/*' : 'image/*,application/pdf');
const formatHint = (formats: string[]) => (isImageFormats(formats) ? 'Photo only' : 'Photo or PDF');

export const CustomerDocumentUpload: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [disclaimer, setDisclaimer] = useState('');
  const [maxMb, setMaxMb] = useState(10);
  const [files, setFiles] = useState<Record<string, File>>({});
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!token) { setErrorMessage('This document link is not valid.'); setStep('error'); return; }
    (async () => {
      try {
        const data = await DocumentRequestAPI.getUploadPage(token);
        setCustomerName(data.customer_name || '');
        setCategories(data.categories || []);
        setDisclaimer(data.disclaimer || '');
        setMaxMb(data.max_file_mb || 10);
        setStep('form');
      } catch (err: any) {
        setErrorMessage(err.message || 'This document link is not available.');
        setStep('error');
      }
    })();
  }, [token]);

  const pick = useCallback((slot: string, file: File | null) => {
    setFormError('');
    setFiles((prev) => {
      const next = { ...prev };
      if (file) next[slot] = file; else delete next[slot];
      return next;
    });
    setPreviews((prev) => {
      const next = { ...prev };
      if (prev[slot]) URL.revokeObjectURL(prev[slot]);
      if (file && file.type.startsWith('image/')) next[slot] = URL.createObjectURL(file);
      else delete next[slot];
      return next;
    });
  }, []);

  // A category is complete when every slot has a file. At least one complete
  // category is required; a partially-filled category blocks submit.
  const completeCount = useMemo(
    () => categories.filter((c) => c.slots.every((s) => files[s])).length,
    [categories, files],
  );

  const validate = (): string => {
    for (const c of categories) {
      const got = c.slots.filter((s) => files[s]).length;
      if (got > 0 && got < c.slots.length) {
        return `${c.label}: please add all parts (${c.slots.map((s) => c.slot_labels[s]).join(' and ')}).`;
      }
    }
    if (completeCount === 0) return 'Please add at least one complete document before submitting.';
    return '';
  };

  const submit = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    if (!token) return;
    setStep('submitting');
    try {
      const fd = new FormData();
      Object.entries(files).forEach(([slot, file]) => fd.append(slot, file, file.name));
      await DocumentRequestAPI.submit(token, fd);
      setStep('success');
    } catch (e: any) {
      setFormError(e.message || 'Something went wrong. Please try again.');
      setStep('form');
    }
  };

  const shell = (inner: React.ReactNode) => (
    <div className="cdu-root">
      <div className="cdu-card">{inner}</div>
      <div className="cdu-secure">🔒 Your information is encrypted and secure.</div>
      <style>{css}</style>
    </div>
  );

  if (step === 'loading') return shell(<div className="cdu-center"><div className="cdu-spinner" /><p>Loading…</p></div>);

  if (step === 'error') return shell(
    <div className="cdu-center">
      <div className="cdu-badge cdu-badge-err">!</div>
      <h1>Link unavailable</h1>
      <p className="cdu-muted">{errorMessage}</p>
    </div>,
  );

  if (step === 'success') return shell(
    <div className="cdu-center">
      <div className="cdu-badge cdu-badge-ok">✓</div>
      <h1>Documents received</h1>
      <p className="cdu-muted">Thank you{customerName ? `, ${customerName}` : ''}. Your documents have been submitted securely. You can close this page.</p>
    </div>,
  );

  const submitting = step === 'submitting';

  return shell(
    <>
      <div className="cdu-head">
        <div className="cdu-brand">AMP Services</div>
        <h1>Upload your documents</h1>
        {customerName ? <p className="cdu-hi">Hi {customerName}, please add the documents below.</p> : null}
      </div>

      <div className="cdu-disclaimer">{disclaimer}</div>

      {categories.map((c) => {
        const done = c.slots.every((s) => files[s]);
        return (
          <section className="cdu-cat" key={c.key}>
            <div className="cdu-cat-head">
              <span className="cdu-cat-title">{c.label}</span>
              <span className={`cdu-chip ${done ? 'cdu-chip-ok' : ''}`}>{done ? 'Complete' : formatHint(c.formats)}</span>
            </div>
            <div className={`cdu-slots ${c.slots.length > 1 ? 'cdu-slots-2' : ''}`}>
              {c.slots.map((s) => {
                const file = files[s];
                const preview = previews[s];
                return (
                  <label className={`cdu-slot ${file ? 'cdu-slot-filled' : ''}`} key={s}>
                    <input
                      type="file"
                      accept={acceptFor(c.formats)}
                      disabled={submitting}
                      onChange={(e) => pick(s, e.target.files?.[0] || null)}
                      hidden
                    />
                    {file ? (
                      <>
                        {preview
                          ? <img className="cdu-thumb" src={preview} alt={c.slot_labels[s]} />
                          : <div className="cdu-fileicon">PDF</div>}
                        <div className="cdu-slot-meta">
                          <span className="cdu-check">✓</span>
                          <span className="cdu-slot-label">{c.slot_labels[s]}</span>
                          <button type="button" className="cdu-remove" onClick={(e) => { e.preventDefault(); pick(s, null); }}>Replace</button>
                        </div>
                      </>
                    ) : (
                      <div className="cdu-slot-empty">
                        <span className="cdu-plus">＋</span>
                        <span className="cdu-slot-label">{c.slot_labels[s]}</span>
                        <span className="cdu-tap">Tap to add</span>
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          </section>
        );
      })}

      {formError ? <div className="cdu-formerr">{formError}</div> : null}

      <button type="button" className="cdu-submit" disabled={submitting || completeCount === 0} onClick={submit}>
        {submitting ? 'Submitting…' : 'Submit documents'}
      </button>
      <p className="cdu-note">Files up to {maxMb} MB each. You can submit one document set; add whichever you have.</p>
    </>,
  );
};

const css = `
  .cdu-root { min-height: 100vh; margin: 0; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px 14px 32px; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; }
  .cdu-card { width: 100%; max-width: 560px; background: #fff; border-radius: 16px; box-shadow: 0 12px 40px rgba(0,0,0,0.22); padding: 24px 20px; box-sizing: border-box; }
  .cdu-secure { color: rgba(255,255,255,0.9); font-size: 13px; margin-top: 16px; }
  .cdu-center { text-align: center; padding: 24px 8px; }
  .cdu-center h1 { font-size: 1.35rem; color: #1a1a2e; margin: 12px 0 6px; }
  .cdu-muted { color: #6b7280; font-size: 0.95rem; line-height: 1.5; }
  .cdu-spinner { width: 38px; height: 38px; border: 4px solid #e5e7eb; border-top-color: #6366f1; border-radius: 50%; margin: 0 auto 12px; animation: cduspin 0.8s linear infinite; }
  @keyframes cduspin { to { transform: rotate(360deg); } }
  .cdu-badge { width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 28px; font-weight: 700; margin: 0 auto; }
  .cdu-badge-ok { background: #dcfce7; color: #16a34a; }
  .cdu-badge-err { background: #fee2e2; color: #dc2626; }
  .cdu-head { text-align: center; margin-bottom: 18px; }
  .cdu-brand { font-weight: 700; letter-spacing: 0.04em; color: #6366f1; font-size: 0.8rem; text-transform: uppercase; }
  .cdu-head h1 { font-size: 1.5rem; color: #1a1a2e; margin: 6px 0 4px; }
  .cdu-hi { color: #4b5563; font-size: 0.95rem; margin: 0; }
  .cdu-disclaimer { background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412; border-radius: 10px; padding: 12px 14px; font-size: 0.85rem; line-height: 1.45; margin-bottom: 18px; }
  .cdu-cat { border: 1px solid #ececf1; border-radius: 12px; padding: 14px; margin-bottom: 14px; }
  .cdu-cat-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .cdu-cat-title { font-weight: 600; color: #1a1a2e; font-size: 1rem; }
  .cdu-chip { font-size: 0.72rem; font-weight: 600; color: #6b7280; background: #f3f4f6; border-radius: 999px; padding: 3px 10px; }
  .cdu-chip-ok { background: #dcfce7; color: #16a34a; }
  .cdu-slots { display: grid; grid-template-columns: 1fr; gap: 10px; }
  .cdu-slots-2 { grid-template-columns: 1fr 1fr; }
  .cdu-slot { border: 1.5px dashed #cbd2e0; border-radius: 10px; min-height: 128px; display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; overflow: hidden; background: #fafbff; transition: border-color 0.15s, background 0.15s; }
  .cdu-slot:hover { border-color: #6366f1; }
  .cdu-slot-filled { border-style: solid; border-color: #16a34a; background: #fff; }
  .cdu-slot-empty { display: flex; flex-direction: column; align-items: center; gap: 4px; color: #6b7280; padding: 14px; text-align: center; }
  .cdu-plus { font-size: 26px; color: #6366f1; line-height: 1; }
  .cdu-slot-label { font-size: 0.85rem; color: #374151; font-weight: 600; }
  .cdu-tap { font-size: 0.72rem; color: #9ca3af; }
  .cdu-thumb { width: 100%; height: 96px; object-fit: cover; }
  .cdu-fileicon { width: 100%; height: 96px; display: flex; align-items: center; justify-content: center; background: #eef2ff; color: #6366f1; font-weight: 700; letter-spacing: 0.08em; }
  .cdu-slot-meta { display: flex; align-items: center; gap: 8px; padding: 8px 10px; width: 100%; box-sizing: border-box; }
  .cdu-check { color: #16a34a; font-weight: 700; }
  .cdu-slot-meta .cdu-slot-label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cdu-remove { background: none; border: none; color: #6366f1; font-size: 0.8rem; font-weight: 600; cursor: pointer; padding: 2px 4px; }
  .cdu-formerr { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; border-radius: 8px; padding: 10px 12px; font-size: 0.85rem; margin: 4px 0 14px; }
  .cdu-submit { width: 100%; background: #6366f1; color: #fff; border: none; border-radius: 10px; padding: 15px; font-size: 1.05rem; font-weight: 700; cursor: pointer; transition: background 0.15s; }
  .cdu-submit:hover:not(:disabled) { background: #5855eb; }
  .cdu-submit:disabled { background: #c7d2fe; cursor: not-allowed; }
  .cdu-note { text-align: center; color: #9ca3af; font-size: 0.75rem; margin: 12px 0 0; }
  @media (max-width: 380px) { .cdu-slots-2 { grid-template-columns: 1fr; } }
  @media (prefers-reduced-motion: reduce) { .cdu-spinner { animation: none; } }
`;

export default CustomerDocumentUpload;
